import { Component, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { AuthService } from "../../../services/api/auth/auth.service";
import { Router } from "@angular/router";
import { MessageService } from "@openng/optimus-ui/api";
import { MessageModule } from "@openng/optimus-ui/message";
import { TooltipModule } from "@openng/optimus-ui/tooltip";
import { SvgIconComponent } from "../../../shared/components/svg-icon/svg-icon.component";
import { LangSwitchComponent } from "../../../shared/components/lang-switch/lang-switch.component";
import { first } from "rxjs";
import { PASSWORD_PATTERN } from "../../../constants/regExp";

@Component({
  standalone: true,
  selector: "app-password-set",
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonComponent,
    MessageModule,
    TooltipModule,
    SvgIconComponent,
    LangSwitchComponent,
  ],
  templateUrl: "./password-set.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./password-set.component.scss",
})
export class PasswordSetComponent {
  private auth = inject(AuthService);
  private router = inject(Router);
  private messageService = inject(MessageService);
  public translate = inject(TranslateService);
  public setPasswordForm: FormGroup = new FormGroup(
    {
      password: new FormControl(null, [Validators.required, Validators.pattern(PASSWORD_PATTERN)]),
      passwordRepeat: new FormControl(null, [Validators.required, Validators.pattern(PASSWORD_PATTERN)]),
    },
    {
      validators: this.validatePasswordMatch(),
    }
  );
  protected readonly saveClicked = signal(false);
  public passwordVisible = signal(false);
  public passwordRepeatVisible = signal(false);

  /**
   * A custom validator function that checks if the password and passwordRepeat
   * fields of a FormGroup have the same value. If they do not, it returns an
   * object with a single key-value pair: { notSamePassword: true }.
   *
   * @param formGroup The FormGroup to validate.
   * @returns An object with the key-value pair { notSamePassword: true } if the
   * password and passwordRepeat fields do not match, otherwise null.
   */
  private validatePasswordMatch(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const password = formGroup.get("password")?.value;
      const passWordRepeat = formGroup.get("passwordRepeat")?.value;
      const valid = password === passWordRepeat;

      if (valid) {
        return null;
      } else {
        return { notSamePassword: true };
      }
    };
  }
  /**
   * Handles the Enter key press event.
   * If the Enter key is pressed, calls the setPassword() method.
   * @param event KeyboardEvent
   */
  onKeyUp(event: KeyboardEvent) {
    if (event.key === "Enter") {
      this.setPassword();
    }
  }

  /**
   * Toggle the visibility of the password input field.
   */
  togglePasswordVisibility() {
    this.passwordVisible.update((value) => !value);
  }

  /**
   * Toggle the visibility of the password input field.
   */
  togglePasswordRepeatVisibility() {
    this.passwordRepeatVisible.update((value) => !value);
  }
  /**
   * Calls AuthService's setPassword method with the password and passwordRepeat
   * field values. If the form is valid and the passwords match, navigates to
   * the login page on success. Otherwise displays an error message.
   */
  setPassword() {
    const password = this.setPasswordForm.get("password")?.value;
    const passwordRepeat = this.setPasswordForm.get("passwordRepeat")?.value;
    this.saveClicked.set(true);

    if (this.setPasswordForm.valid && password && passwordRepeat) {
      if (password === passwordRepeat) {
        this.auth
          .setPassword(password)
          .pipe(first())
          .subscribe({
            next: (res) => {
              if (res.success) {
                this.messageService.add({
                  key: "global",
                  severity: "success",
                  summary: this.translate.instant("common.success"),
                  detail: this.translate.instant("auth.password_set.successful_message"),
                  life: 5000,
                });
                this.router.navigateByUrl("/login");
              }
            },
          });
      } else {
        this.messageService.add({
          key: "global",
          severity: "error",
          summary: this.translate.instant("common.error"),
          detail: this.translate.instant("auth.password_set.password_mismatch"),
          life: 5000,
        });
      }
    }
  }
}
